import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userProfileApiClient } from 'src/api/FiretrolApiClientInstances';
import { invalidateFor } from 'src/api/queries/invalidationMap';

/**
 * `useAuth`'s user context isn't wired to the react-query cache, so callers
 * refresh it via `loadUserFromCache()` on success rather than reading the
 * mutation's resolved `UserDto`. OTP-flow mutations (request/verify) are
 * `meta: { silent: true }` since their errors are surfaced inline by
 * `PhoneVerificationStepper`, not as a global toast.
 */

export function useUpdateNotificationChannels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channels: string[]) =>
      userProfileApiClient.updateNotificationChannels({ channels }),
    meta: { successMessage: 'Notification preferences updated.' },
    onSettled: () => invalidateFor('user.update', qc),
  });
}

export function useDeclineSmsConsent() {
  const qc = useQueryClient();
  return useMutation({
    // The only decision value the backend accepts from this route.
    mutationFn: () =>
      userProfileApiClient.updateSmsConsent({ decision: 'declined' }),
    meta: { silent: true }, // the interstitial owns its own UX for this
    onSettled: () => invalidateFor('user.update', qc),
  });
}

export function useOptOutOfSms() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => userProfileApiClient.optOutOfSms(),
    meta: { successMessage: 'Text message notifications turned off.' },
    onSettled: () => invalidateFor('user.update', qc),
  });
}

export function useRequestPhoneVerification() {
  return useMutation({
    mutationFn: (phoneNumber: string) =>
      userProfileApiClient.requestPhoneVerification({ phoneNumber }),
    meta: { silent: true },
  });
}

export function useVerifyPhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) => userProfileApiClient.verifyPhone({ code }),
    meta: { silent: true },
    onSettled: () => invalidateFor('user.update', qc),
  });
}

export function useDeletePhone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => userProfileApiClient.deletePhone(),
    meta: { successMessage: 'Phone number removed.' },
    onSettled: () => invalidateFor('user.update', qc),
  });
}
